import { defineComponent, computed, ref, watch, nextTick, openBlock, createBlock, unref, withCtx, createVNode, normalizeClass, createElementVNode, createElementBlock, Fragment, createCommentVNode } from "vue";
import { parseDate, DatePickerRoot, DatePickerControl, DatePickerInput, DatePickerTrigger } from "@ark-ui/vue/date-picker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import IconCalendar from "../../assets/icons/calendar.svg.js";
import { useFormControl } from "../../composables/useFormControl.js";
import PickerView from "./components/PickerView.vue.js";
import { useDatePickerLocale } from "./useDatePickerLocale.js";
const _hoisted_1 = { class: "elfie-datepicker__control-wrapper" };
const _hoisted_2 = ["value", "disabled"];
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "DatePicker",
  props: {
    modelValue: { default: null },
    dateFormat: { default: "DD/MM/YYYY" },
    showTime: { type: Boolean, default: true },
    showFooter: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    min: { default: void 0 },
    max: { default: void 0 },
    locale: { default: void 0 }
  },
  emits: ["update:modelValue"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const pickerLocale = useDatePickerLocale(() => props.locale);
    const dateInputPlaceholder = computed(() => {
      const reference = dayjs("2000-06-15");
      return reference.locale(pickerLocale.value).format(props.dateFormat);
    });
    const emit = __emit;
    const { model, hasError } = useFormControl(props, emit);
    const isOpen = ref(false);
    const dateValue = ref("");
    const timeValue = ref("");
    const isUpdating = ref(false);
    const timeInputRef = ref(null);
    const dateInputRef = ref(null);
    const timeZone = ref(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const initialValueOnOpen = ref(null);
    const isCommittingFooterAction = ref(false);
    const formatDateValue = (dateValue2, details) => {
      if (!dateValue2) return "";
      try {
        const date = dayjs(dateValue2.toString()).locale(details.locale);
        if (date.isValid()) {
          return date.format(props.dateFormat);
        }
        return "";
      } catch {
        return "";
      }
    };
    const handleTimeBlur = (event) => {
      const input = event.target;
      const rawValue = input.value;
      let hoursStr = "";
      let minutesStr = "";
      if (rawValue.includes(":")) {
        const [hoursPart, minutesPart] = rawValue.split(":");
        const hoursDigits = (hoursPart || "").replace(/\D/g, "");
        const minutesDigits = (minutesPart || "").replace(/\D/g, "");
        hoursStr = hoursDigits && Number(hoursDigits[0]) > 2 ? hoursDigits[0] || "" : hoursDigits.slice(0, 2) || "";
        minutesStr = minutesDigits && Number(minutesDigits[0]) > 5 ? minutesDigits[0] || "" : minutesDigits.slice(0, 2) || "";
      } else {
        const digits = rawValue.replace(/\D/g, "").slice(0, 4);
        if (digits.length === 0) {
          hoursStr = "";
          minutesStr = "";
        } else if (digits.length === 1 || Number(digits[0]) > 2) {
          hoursStr = digits[0] || "";
          minutesStr = digits.slice(1, 3) || "";
        } else {
          hoursStr = digits.slice(0, 2) || "";
          minutesStr = digits.slice(2, 4) || "";
        }
        if (minutesStr && Number(minutesStr[0]) > 5) {
          minutesStr = minutesStr[0] || "";
        }
      }
      let hours = Number(hoursStr) || 0;
      let minutes = Number(minutesStr) || 0;
      hours = hours > 23 ? hours % 24 : hours;
      minutes = minutes > 59 ? minutes % 60 : minutes;
      const formattedValue = `${to2Digit(hours)}:${to2Digit(minutes)}`;
      timeValue.value = formattedValue;
      input.value = formattedValue;
    };
    const to2Digit = (num) => {
      return num < 10 ? "0" + num : num;
    };
    const buildIsoFromDraft = () => {
      if (!dateValue.value) return null;
      let dayjsDate = dayjs(dateValue.value);
      if (!dayjsDate.isValid()) return null;
      if (props.showTime && timeValue.value) {
        const timeParts = timeValue.value.split(":").map(Number);
        const hours = timeParts[0];
        const minutes = timeParts[1];
        if (hours !== void 0 && minutes !== void 0 && !isNaN(hours) && !isNaN(minutes)) {
          dayjsDate = dayjsDate.hour(hours).minute(minutes).second(0).millisecond(0);
        } else {
          dayjsDate = dayjsDate.hour(0).minute(0).second(0).millisecond(0);
        }
      } else {
        dayjsDate = dayjsDate.hour(0).minute(0).second(0).millisecond(0);
      }
      return dayjsDate.toISOString();
    };
    const clearDisabled = computed(() => !dateValue.value);
    const hasDraftChanges = computed(() => {
      return buildIsoFromDraft() !== initialValueOnOpen.value;
    });
    const doneDisabled = computed(() => {
      return !dateValue.value || !hasDraftChanges.value;
    });
    const syncLocalFromModelValue = (value) => {
      if (value) {
        const dayjsDate = dayjs(value);
        if (dayjsDate.isValid()) {
          dateValue.value = dayjsDate.format("YYYY-MM-DD");
          if (props.showTime) {
            timeValue.value = dayjsDate.format("HH:mm");
          }
          return;
        }
      }
      dateValue.value = "";
      timeValue.value = "";
    };
    const handleTimeFocus = (event) => {
      const input = event.target;
      input.select();
    };
    const hasUserOpenedPicker = ref(false);
    const handleValueChange = (details) => {
      if (!details || !details.value || Array.isArray(details.value) && details.value.length === 0) {
        dateValue.value = "";
        return;
      }
      const newValue = Array.isArray(details.value) ? details.value[0] : details.value;
      const newStr = newValue.toString();
      if (!hasUserOpenedPicker.value && props.min && modelValue.value) {
        const minStr = dayjs(props.min).format("YYYY-MM-DD");
        const currentStr = dayjs(modelValue.value).format("YYYY-MM-DD");
        if (newStr === minStr && dayjs(currentStr).isBefore(dayjs(minStr))) {
          return;
        }
      }
      dateValue.value = newStr;
    };
    const handleInputClick = () => {
      if (!props.disabled) {
        isOpen.value = true;
      }
    };
    const modelValue = computed(() => {
      return model.value ?? null;
    });
    const hasErrorState = computed(() => {
      return hasError?.value ?? false;
    });
    const dateModelValue = computed(() => {
      if (!dateValue.value) return [];
      try {
        return [parseDate(dateValue.value)];
      } catch {
        return [];
      }
    });
    const minDate = computed(() => {
      if (!props.min) return void 0;
      return parseDate(props.min);
    });
    const maxDate = computed(() => {
      if (!props.max) return void 0;
      return parseDate(props.max);
    });
    const handleClear = () => {
      if (clearDisabled.value) return;
      isCommittingFooterAction.value = true;
      isUpdating.value = true;
      dateValue.value = "";
      timeValue.value = "";
      model.value = null;
      initialValueOnOpen.value = null;
      isOpen.value = false;
      nextTick(() => {
        isUpdating.value = false;
      });
    };
    const handleDone = () => {
      if (doneDisabled.value) return;
      isCommittingFooterAction.value = true;
      const isoString = buildIsoFromDraft();
      if (!isoString) return;
      model.value = isoString;
      isOpen.value = false;
    };
    watch(
      modelValue,
      (newValue, oldValue) => {
        if (newValue === oldValue) return;
        if (newValue) {
          const dayjsDate = dayjs(newValue);
          if (dayjsDate.isValid()) {
            const dateStr = dayjsDate.format("YYYY-MM-DD");
            if (dateValue.value !== dateStr) {
              isUpdating.value = true;
              dateValue.value = dateStr;
              if (props.showTime) {
                const timeStr = dayjsDate.format("HH:mm");
                if (timeValue.value !== timeStr) {
                  timeValue.value = timeStr;
                }
              }
              nextTick(() => {
                isUpdating.value = false;
              });
            } else {
              if (props.showTime) {
                const timeStr = dayjsDate.format("HH:mm");
                if (timeValue.value !== timeStr) {
                  timeValue.value = timeStr;
                }
              }
            }
          }
        } else {
          if (dateValue.value.length > 0) {
            isUpdating.value = true;
            dateValue.value = "";
            timeValue.value = "";
            nextTick(() => {
              isUpdating.value = false;
            });
          }
        }
      },
      { immediate: true }
    );
    watch(
      dateValue,
      (newValue) => {
        if (isUpdating.value) return;
        if (newValue && !props.showFooter) {
          setTimeout(() => {
            isOpen.value = false;
          }, 150);
        }
        if (props.showFooter) return;
        if (newValue) {
          const isoString = buildIsoFromDraft();
          if (isoString) {
            model.value = isoString;
            isUpdating.value = false;
          }
        } else {
          model.value = null;
          isUpdating.value = false;
        }
      },
      { deep: true }
    );
    watch(timeValue, (newValue) => {
      if (isUpdating.value || !props.showTime || props.showFooter) return;
      if (dateValue.value) {
        let dayjsDate = dayjs(dateValue.value);
        if (dayjsDate.isValid() && newValue) {
          const timeParts = newValue.split(":").map(Number);
          const hours = timeParts[0];
          const minutes = timeParts[1];
          if (hours !== void 0 && minutes !== void 0 && !isNaN(hours) && !isNaN(minutes)) {
            isUpdating.value = true;
            dayjsDate = dayjsDate.hour(hours).minute(minutes).second(0).millisecond(0);
            const isoString = dayjsDate.toISOString();
            model.value = isoString;
            isUpdating.value = false;
          }
        }
      }
    });
    watch(isOpen, (newValue) => {
      if (newValue) {
        hasUserOpenedPicker.value = true;
        initialValueOnOpen.value = modelValue.value ? dayjs(modelValue.value).toISOString() : null;
        if (props.disabled) {
          isOpen.value = false;
        }
      }
      if (!newValue) {
        if (props.showFooter && !isCommittingFooterAction.value) {
          isUpdating.value = true;
          syncLocalFromModelValue(modelValue.value);
          nextTick(() => {
            isUpdating.value = false;
          });
        }
        isCommittingFooterAction.value = false;
        nextTick(() => {
          if (dateInputRef.value) {
            const inputEl = dateInputRef.value.$el ?? dateInputRef.value;
            if (inputEl && typeof inputEl.blur === "function") {
              inputEl.blur();
            }
          }
          if (timeInputRef.value) {
            timeInputRef.value.blur();
          }
        });
      }
    });
    dayjs.extend(customParseFormat);
    return (_ctx, _cache) => {
      return openBlock(), createBlock(unref(DatePickerRoot), {
        open: isOpen.value,
        "onUpdate:open": _cache[0] || (_cache[0] = ($event) => isOpen.value = $event),
        "model-value": dateModelValue.value,
        "num-of-months": 1,
        format: formatDateValue,
        disabled: __props.disabled,
        "close-on-select": !__props.showFooter,
        min: minDate.value,
        max: maxDate.value,
        "start-of-week": 1,
        "time-zone": timeZone.value,
        locale: unref(pickerLocale),
        class: "elfie-datepicker",
        onValueChange: handleValueChange
      }, {
        default: withCtx(() => [
          createVNode(unref(DatePickerControl), {
            class: normalizeClass([
              "elfie-datepicker__control",
              {
                "elfie-datepicker__control--open": isOpen.value,
                "elfie-datepicker__control--disabled": __props.disabled,
                "elfie-datepicker__control--error": hasErrorState.value
              }
            ])
          }, {
            default: withCtx(() => [
              createElementVNode("div", _hoisted_1, [
                createVNode(unref(DatePickerInput), {
                  ref_key: "dateInputRef",
                  ref: dateInputRef,
                  class: "elfie-datepicker__input",
                  disabled: __props.disabled,
                  placeholder: dateInputPlaceholder.value,
                  onClick: handleInputClick
                }, null, 8, ["disabled", "placeholder"]),
                __props.showTime ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
                  _cache[1] || (_cache[1] = createElementVNode("div", { class: "elfie-datepicker__divider" }, null, -1)),
                  createElementVNode("input", {
                    ref_key: "timeInputRef",
                    ref: timeInputRef,
                    value: timeValue.value,
                    type: "text",
                    class: "elfie-datepicker__time-input",
                    disabled: __props.disabled,
                    placeholder: "00:00",
                    onFocus: handleTimeFocus,
                    onBlur: handleTimeBlur
                  }, null, 40, _hoisted_2)
                ], 64)) : createCommentVNode("", true)
              ]),
              createVNode(unref(DatePickerTrigger), {
                class: "elfie-datepicker__trigger",
                disabled: __props.disabled
              }, {
                default: withCtx(() => [
                  createVNode(unref(IconCalendar), { class: "elfie-datepicker__icon elfie-datepicker__icon--calendar" })
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            _: 1
          }, 8, ["class"]),
          createVNode(PickerView, {
            "show-footer": __props.showFooter,
            "clear-disabled": clearDisabled.value,
            "done-disabled": doneDisabled.value,
            onClear: handleClear,
            onDone: handleDone
          }, null, 8, ["show-footer", "clear-disabled", "done-disabled"])
        ]),
        _: 1
      }, 8, ["open", "model-value", "disabled", "close-on-select", "min", "max", "time-zone", "locale"]);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=DatePicker.vue2.js.map
